import React, { PropsWithChildren } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import { default as MuiSnackbar, SnackbarOrigin } from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { Omit } from '@yi/core';

import { darkGrey, errorRed, snackbarZIndex, turquoise } from '~/styles';

import { Snackbar_, SnackbarHeader } from './elements';

interface SnackbarProps extends Omit<React.ComponentProps<typeof MuiSnackbar>, 'children'> {
  anchorOrigin?: SnackbarOrigin;
  alignRightMobile?: boolean;
  headerText?: string | null;
  hideCloseIcon?: boolean;
  autoHide?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  variant?: 'red' | 'white' | undefined;
  setClose(): void;
  children?: React.ReactNode | undefined;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  contentInnerStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
}

export const SnackbarCloseIcon: React.FC<
  PropsWithChildren<{
    variant?: 'red' | 'white' | undefined;
    setClose(): void;
  }>
> = ({ variant, setClose }) => {
  return (
    <Box color={darkGrey} textAlign="right">
      <Fab
        size="small"
        color="inherit"
        aria-label="close"
        style={{ background: variant && variant === 'red' ? errorRed : turquoise }}
        onClick={() => setClose()}
      >
        <CloseIcon />
      </Fab>
    </Box>
  );
};

export const Snackbar: React.FC<PropsWithChildren<SnackbarProps>> = ({
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  headerText,
  hideCloseIcon,
  autoHide,
  textAlign,
  setClose,
  variant,
  open,
  children,
  style = {},
  contentStyle = {},
  contentInnerStyle = { minWidth: '240px', maxWidth: '400px' },
  alignRightMobile = false,
  headerStyle = {},
  ...props
}) => {
  return (
    <Snackbar_
      open={open}
      style={{ zIndex: snackbarZIndex, ...style }}
      autoHideDuration={autoHide ? 8000 : null}
      anchorOrigin={anchorOrigin}
      alignRightMobile={alignRightMobile}
      {...props}
    >
      <Box mx={{ xs: 'auto', sm: 'auto' }} position="relative">
        <SnackbarContent
          style={contentStyle}
          message={
            <Box mx="auto" pr={0} style={contentInnerStyle}>
              <Grid container>
                <Grid item xs={textAlign === 'center' ? 12 : 11}>
                  <Box textAlign={textAlign || 'left'}>
                    {headerText && (
                      <SnackbarHeader
                        variant={variant}
                        style={headerStyle}
                        data-testid="article-paywall-remaining"
                      >
                        {headerText}
                      </SnackbarHeader>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  {!hideCloseIcon && (
                    <Box position="absolute" top={0} right={0} mt={-1.5} mr={-1.9}>
                      <SnackbarCloseIcon variant={variant} setClose={setClose} />
                    </Box>
                  )}
                </Grid>
              </Grid>
              {children}
            </Box>
          }
        />
      </Box>
    </Snackbar_>
  );
};
